/* eslint-disable react-hooks/exhaustive-deps */
import { DocumentNode, gql, useLazyQuery } from '@apollo/client';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Chip, SxProps } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { ClientGroup, User } from '../../../interfaces';
import { UserContext } from '../../../providers/userContextProvider';
import { Autocomplete } from '../../2-component/autocomplete/autocomplete';
import { Box, Typography } from '../../1-primative';

const MAX_SEARCH_RESULTS = 12;
export type Item = Required<{ id: string; name: string; primaryUser: User }>;
export type SearchResults = { items: Item[]; count: number };
export interface SearchFn {
  (query: string): Promise<SearchResults>;
}

export const SEARCH_HOUSEHOLD = gql`
  query searchClientGroups($searchText: String!, $organizationId: ObjectID!) {
    fetchClientGroups(input: {
      filter: { searchText: $searchText, organizationId: $organizationId, type: HOUSEHOLD }
      pagination: { perPage: ${MAX_SEARCH_RESULTS} },
    }) {
      totalCount
      clientGroups {
        id
        name
        primaryUser { firstName middleName lastName }
        relationships {
          type
          user { id }
          accessType
        }
      }
    }
  }
`;

const matchingFn = (item: Item, value: string) => [item.id, item.name].some((str) => str?.toLowerCase()?.includes(value.toLowerCase()));

export const HouseholdSelect = ({
  household,
  setHousehold,
  label,
  disabled,
  fullWidth,
  labelSx,
  width,
  error,
  errorText,
  customSearchFn,
  onBlur,
  autoFocus,
  customSearchQuery,
  setDisableEditForm,
}: {
  household: Partial<ClientGroup> | undefined;
  setHousehold: (household: ClientGroup | undefined) => void;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
  labelSx?: SxProps;
  width?: string;
  error?: boolean;
  errorText?: string;
  customSearchFn?: SearchFn;
  onBlur?: (e: React.ChangeEvent) => void;
  autoFocus?: boolean;
  customSearchQuery?: DocumentNode;
  setDisableEditForm?: (disableEditForm: boolean) => void;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const [searchResult, setSearchResult] = useState<SearchResults>({ items: [], count: 0 });
  const [accessibleHouseholdIds, setAccessibleHouseholdIds] = useState<string[]>([]);

  const [searchHouseholds, { loading }] = useLazyQuery(customSearchQuery ?? SEARCH_HOUSEHOLD, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const defaultSearchFn = (query: string): Promise<SearchResults> => new Promise<SearchResults>((resolve) => {
    if (disabled) return;
    searchHouseholds({
      variables: {
        searchText: query,
        organizationId: activeOrganization.id,
      },
      onCompleted: (data) => {
        const totalCount = Number(data.fetchClientGroups.totalCount);
        const clientGroups = data.fetchClientGroups.clientGroups || [];
        setAccessibleHouseholdIds(clientGroups.map((accessibleHousehold: Item) => accessibleHousehold.id));
        resolve({
          items: clientGroups,
          count: totalCount,
        });
      },
      onError: () => {
        resolve({
          items: [],
          count: 0,
        });
      },
    });
  });

  const searchFn = customSearchFn ?? defaultSearchFn;

  // Initial query to prevent empty dropdown
  const initSearchResult = async () => {
    setSearchResult(await searchFn(''));
  };

  useEffect(() => {
    initSearchResult();
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
      initSearchResult();
    }
  }, [disabled, autoFocus]);

  return (
    <Autocomplete
      width={width}
      data-testid='householdSelectField'
      freeSolo
      loading={loading}
      disabled={disabled}
      options={searchResult.items}
      fullWidth={fullWidth}
      label={label ?? ''}
      value={household}
      getOptionLabel={(option) => option.name}
      renderOption={(props: any, option: any) => (
        <li {...props}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant='bodyLarge'>{option.name}</Typography>
          </Box>
        </li>
      )}
      isOptionEqualToValue={(option: Item, value: Item) => option.id === value.id}
      onInputChange={async (e: React.SyntheticEvent, value: string) => setSearchResult(await searchFn(value))}
      onChange={(e: React.SyntheticEvent, value: Item, reason: string) => {
        if (reason === 'clear') {
          setHousehold(undefined);
          setDisableEditForm?.(false);
        } else if (value.id) {
          const result = searchResult.items.find((item) => item.id === value.id);
          setHousehold(result as ClientGroup);
          if (result && !accessibleHouseholdIds?.includes(value.id)) {
            setDisableEditForm?.(true);
          } else {
            setDisableEditForm?.(false);
          }
        }
      }}
      onBlur={onBlur}
      filterOptions={(options: Item[], state: any) => options.filter((item) => matchingFn(item, state?.inputValue))}
      renderTags={(tagValue: any, getTagProps: any) => tagValue.map((option: any, index: number) => (
        <Chip label={option} sx={{ marginTop: '-2px !important' }} {...getTagProps({ index })} size='small' onDelete={undefined} />
      ))}
      componentsProps={{ popupIndicator: { sx: { display: 'none' } } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        inputRef,
        disabled,
      }}
      labelSx={labelSx}
      error={error}
      errorText={errorText}
      autoFocus={autoFocus}
    />
  );
};
